import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { startOfWeek, endOfWeek, addWeeks, eachDayOfInterval, format, isSameDay } from 'date-fns';
import { DateRangePicker } from '@mui/x-date-pickers-pro';
import { Autocomplete, Dialog, DialogContent, IconButton, InputAdornment, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getProjectLoadingSubject, Project } from '../../../store/Project';
import { BORDER_STYLE } from '../../../utils/styleHelpers';
import { getDate } from '../../../utils/helpers';
import { SrOnlyText, TextBlock, TextSpan } from '../../Atoms/Text';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { Icon } from '../../Atoms/Icon';
import { InventoryAsset } from '../../../types';
import { getInventoryByProjectId, getInventoryLoadingSubject } from '../../../store/Inventory';

export interface KeyDatesWidgetProps {
  projects: Project[];
  isHome?: boolean;
}

type KeyDate = { dateEvents: { location: string; projectId?: string; assetId: string }[] };
type KeyDates = Record<string, KeyDate>;

enum PresetDateRanges {
  THIS_WEEK = 'This Week',
  NEXT_TWO_WEEKS = 'Next Two Weeks'
}

export const KeyDatesWidget = ({ projects, isHome }: KeyDatesWidgetProps) => {
  const [projectsLoading, setProjectsLoading] = useState<boolean>(false);
  const [assetLoading, setAssetLoading] = useState<boolean>(false);
  const [projectAssets, setProjectAssets] = useState<InventoryAsset[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(PresetDateRanges.THIS_WEEK);
  const [keyDates, setKeyDates] = useState<KeyDates>({});
  const [openDateRangePicker, setOpenDateRangePicker] = useState<boolean>(false);
  const [customDateStart, setCustomDateStart] = useState<Date | null>(null);
  const [customDateEnd, setCustomDateEnd] = useState<Date | null>(null);

  const formatKeyDatesForPeriod = (dates: string[]) => {
    return dates.reduce((acc, date): KeyDates => {
      acc[date] = { dateEvents: [] };
      projectAssets.forEach((ast) => {
        if (ast.focDate && isSameDay(getDate(ast.focDate), new Date(date))) {
          acc[date].dateEvents.push({
            location: ast.addressInfo.number,
            projectId: ast.projectId,
            assetId: ast.id
          });
        }
      });

      if (acc[date].dateEvents.length === 0) {
        delete acc[date]; // Remove dates with no events
      }
      return acc;
    }, {} as KeyDates);
  };

  const getKeyDatesByStartAndEndDate = (start: Date, end: Date) => {
    const dates = eachDayOfInterval({ start, end });

    const allDates = dates.map((date) => format(date, 'MM/dd/yyyy'));
    const keyDates = formatKeyDatesForPeriod(allDates);
    setKeyDates(keyDates);
  };

  const getKeyDatesForPeriod = (weeks: 1 | 2) => {
    const today = new Date();

    const start = startOfWeek(today, { weekStartsOn: 1 });
    const end = endOfWeek(addWeeks(start, weeks - 1), { weekStartsOn: 1 });

    getKeyDatesByStartAndEndDate(start, end);
  };

  useEffect(() => {
    const loadingSub = getProjectLoadingSubject().subscribe((loading) => {
      setProjectsLoading(loading);
    });
    const assetLoadingSub = getInventoryLoadingSubject().subscribe((loading) => {
      setAssetLoading(loading);
    });

    getKeyDatesForPeriod(selectedPeriod === PresetDateRanges.THIS_WEEK ? 1 : 2);

    return () => {
      if (loadingSub) loadingSub.unsubscribe();
      if (assetLoadingSub) assetLoadingSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!assetLoading && !projectsLoading) {
      const allAssets = projects.flatMap((project) => getInventoryByProjectId(project.id));
      setProjectAssets(allAssets);
    }
  }, [projects.length, assetLoading, projectsLoading]);

  useEffect(() => {
    if (selectedPeriod === PresetDateRanges.THIS_WEEK) {
      getKeyDatesForPeriod(1);
    } else if (selectedPeriod === PresetDateRanges.NEXT_TWO_WEEKS) {
      getKeyDatesForPeriod(2);
    }
  }, [selectedPeriod, projectAssets.length]);

  useEffect(() => {
    if (customDateStart && customDateEnd) {
      setSelectedPeriod(`${format(customDateStart, 'MM/dd/yyyy')} - ${format(customDateEnd, 'MM/dd/yyyy')}`);
      getKeyDatesByStartAndEndDate(customDateStart, customDateEnd);
    }
  }, [customDateStart, customDateEnd]);

  return (
    <div
      className={`${BORDER_STYLE} w-96 pt-3 bg-white`}
      data-cy="key-dates-widget">
      <p
        aria-level={2}
        role="heading"
        className="text-sm font-medium tracking-[0.6px] text-grey-5 text-center leading-[15px]">
        Upcoming FOC Dates
      </p>
      {(projectsLoading || assetLoading) && (
        <div className={`flex justify-center items-center h-32 ${!isHome && 'w-full'}`}>
          <ComponentSpinner />
        </div>
      )}
      {!projects.length && !projectsLoading && !assetLoading && (
        <div className={`h-32 ${!isHome && 'w-64'} text-center py-14`}>
          <TextBlock size="sm14">No data available</TextBlock>
        </div>
      )}
      {!projectsLoading && !assetLoading && projects.length > 0 && (
        <div className="text-center mb-2 widget-container custom-widget h-[200px] m-auto text-[10px] overflow-y-hidden">
          <div className="mx-3.5 mb-[9px] px-4">
            <div className="flex flex-col justify-between items-center w-[100%]">
              <div className="w-full">
                <Autocomplete
                  id="key-dates-period-select"
                  autoSelect
                  options={[PresetDateRanges.THIS_WEEK, PresetDateRanges.NEXT_TWO_WEEKS]}
                  value={selectedPeriod}
                  onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                  onChange={(event, newValue) => setSelectedPeriod(newValue)}
                  size={'small'}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      InputProps={{
                        ...params.InputProps,
                        style: { fontSize: '.875rem', borderRadius: '4px', width: '100%', paddingRight: '1rem' },
                        name: 'key-dates-period-select',
                        endAdornment: (
                          <InputAdornment position="end">
                            <div>
                              <button onClick={() => setOpenDateRangePicker(true)}>
                                <Icon
                                  type="calendar"
                                  size="p-2"
                                />
                                <SrOnlyText>Open custom date range picker</SrOnlyText>
                              </button>
                              <Dialog
                                open={openDateRangePicker}
                                onClose={() => setOpenDateRangePicker(false)}>
                                <IconButton
                                  aria-label="close"
                                  onClick={() => setOpenDateRangePicker(false)}
                                  sx={{
                                    position: 'absolute',
                                    right: 8,
                                    top: 4,
                                    color: (theme) => theme.palette.grey[500]
                                  }}>
                                  <CloseIcon />
                                </IconButton>
                                <DialogContent>
                                  <div className="p-2">
                                    <TextBlock
                                      className="mb-4"
                                      size="sm14">
                                      Select Custom Date Range
                                    </TextBlock>
                                    <DateRangePicker
                                      defaultValue={[
                                        customDateStart || startOfWeek(new Date()),
                                        customDateEnd || endOfWeek(new Date())
                                      ]}
                                      onChange={(range) => {
                                        setCustomDateStart(range[0]);
                                        setCustomDateEnd(range[1]);
                                      }}
                                      onClose={() => setOpenDateRangePicker(false)}
                                    />
                                  </div>
                                </DialogContent>
                              </Dialog>
                            </div>
                          </InputAdornment>
                        )
                      }}
                      variant="outlined"
                      required={true}
                      margin="normal"
                    />
                  )}
                />
              </div>
              <div className="w-full h-[130px] overflow-y-auto overflow-x-visible z-0">
                {Object.keys(keyDates).length === 0 && (
                  <div className="flex flex-col items-center justify-center h-full">
                    <TextBlock
                      color="grey6"
                      size="sm14">
                      No key dates match your selected timeframe.
                    </TextBlock>
                  </div>
                )}
                {Object.keys(keyDates).length > 0 &&
                  Object.entries(keyDates).map(([date, keyDate]) => (
                    <KeyDateItem
                      key={date}
                      date={date}
                      keyDate={keyDate}
                    />
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface KeyDateItemProps {
  date: string;
  keyDate: KeyDate;
}

const KeyDateItem = ({ date, keyDate }: KeyDateItemProps) => {
  return (
    <div className="px-1">
      <div className="relative w-full border-l-[1px] border-grey-6 pl-8 pb-2">
        <div className="absolute left-[-5px] top-6 p-[5px] bg-indigo rounded-full w-[2px] z-20"></div>
        <div className="relative flex flex-row justify-start items-start rounded border-[1px] p-2 w-full">
          <div className="absolute left-[-10px] top-[18px] w-[20px] h-[20px] border-l-[1px] border-b-[1px] bg-white z-20 rotate-45"></div>
          <div className="text-left mr-8">
            <TextSpan
              size="xs"
              color="grey4">
              {date}
            </TextSpan>
          </div>
          <ul className="flex flex-col">
            {keyDate.dateEvents.map((event, index) => (
              <li key={index}>
                <Link
                  to={`/projects/${event.projectId}?detailId=${event.assetId}`}
                  className="flex flex-col items-start mb-2">
                  <TextBlock size="sm14">{event.location || '-'}</TextBlock>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
